import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async'; // Import Helmet
import { ThemeProvider } from './contexts/ThemeContext'; // Theme management
import './styles/App.css'; // Global styles
import Home from './pages/Home'; // Home page component
import About from './pages/About'; // About page component
import FAQs from './pages/FAQs'; // FAQspage component
import Facility from './pages/Facility'; // Facility page component
import AreYouACoach from './pages/Coach'; // Coach page component
import VolleyballClubs from './pages/VolleyballClubs'; // Volleyball clubs landing page
import ClubSignup from './pages/ClubSignup';
import ClubLanding from './pages/ClubLanding';
import ClubDashboard from './pages/ClubDashboard';
import ClubManage from './pages/ClubManage';
import ClubReports from './pages/ClubReports';
import CoachSignup from './pages/CoachSignup';
import CoachLanding from './pages/CoachLanding';
import CoachDashboard from './pages/CoachDashboard';
import Renew from './pages/Renew';
import PlansPricing from './pages/Plans'; // PlansPricing page component
import Programs from './pages/Programs'; // Programs page component
import Periodization from './pages/Periodization'; // Periodization page component
import Drops from './pages/Drops'; // Drops page component
import Equipment from './pages/Equipment'; // Equipment recommendations page
import AppPage from './pages/App'; // Download app page
import ProForma from './pages/ProForma'; // Fieldhouse pro forma calculator
import Support from './pages/Support'; // Support page component
import DeleteAccount from './pages/DeleteAccount'; // Delete Account page
import Terms from './pages/Terms'; // Terms & Conditions page
import Privacy from './pages/Privacy'; // Privacy Policy page
import ReleaseNotes from './pages/ReleaseNotes'; // Release Notes page
import AuthPage from './pages/AuthPage'; // Sign Up / Sign In page
import OnboardingFlow from './pages/OnboardingFlow'; // Onboarding wizard
import Dashboard from './pages/Dashboard'; // Dashboard page
import Calendar from './pages/Calendar'; // Calendar page
import Settings from './pages/Settings'; // Settings page
import SetupProgram from './pages/SetupProgram'; // Program setup (admin)
import Program from './pages/Program'; // Program page
import ChangeProgram from './pages/ChangeProgram'; // Change program page
import Performance from './pages/Performance'; // Performance tracking page
import Awards from './pages/Awards'; // Awards and streaks page
import Notifications from './pages/Notifications'; // Notifications page
import Workout from './pages/Workout'; // Active workout tracking page
import AppEntry from './pages/AppEntry'; // PWA entry point
import AdminLayout from './pages/Admin/AdminLayout'; // Admin zone (isolated from marketing/post-auth)

import './components/ripple.js';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]); // Runs every time the route changes

  return null;
}

function App() {
  return (
    <ThemeProvider>
    <HelmetProvider>
      <div className="App">
        {/* Helmet for setting default metadata - page-specific SEO components override these */}
        <Helmet>
          <title>Begin Strength</title>
          <meta name="description" content="A functional strength training facility for coaches and their athletes." />

          {/* Open Graph Meta Tags for Social Media */}
          <meta property="og:title" content="Begin Strength | Functional Strength Training" />
          <meta property="og:description" content="A functional strength training facility for coaches and their athletes." />
          <meta property="og:image" content="https://beginthe.work/social-media-image.png" />
          <meta property="og:url" content="https://beginthe.work/" />
          <meta property="og:type" content="website" />

          {/* Twitter Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Begin Strength | Functional Strength Training" />
          <meta name="twitter:description" content="A functional strength training facility for coaches and their athletes." />
          <meta name="twitter:image" content="https://beginthe.work/social-media-image.png" />
          <meta name="twitter:url" content="https://beginthe.work/" />
        </Helmet>

        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/facility" element={<Facility />} />
            <Route path="/plans" element={<PlansPricing />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/coach" element={<AreYouACoach />} />
            <Route path="/coach/signup" element={<CoachSignup />} />
            <Route path="/coach/:slug" element={<CoachLanding />} />
            <Route path="/clubs" element={<VolleyballClubs />} />
            <Route path="/clubs/signup" element={<ClubSignup />} />
            <Route path="/clubs/:slug" element={<ClubLanding />} />
            <Route path="/drops" element={<Drops />} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/app" element={<AppPage />} />
            <Route path="/pro-forma" element={<ProForma />} />
            <Route path="/periodization" element={<Periodization />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/support" element={<Support />} />
            <Route path="/delete" element={<DeleteAccount />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/release-notes" element={<ReleaseNotes />} />
            <Route path="/admin" element={<Navigate to="/admin/content/workouts" replace />} />
            <Route path="/admin/programs" element={<AdminLayout />} />
            <Route path="/admin/programs/new" element={<AdminLayout />} />
            <Route path="/admin/programs/:programId/edit" element={<AdminLayout />} />
            <Route path="/admin/clubs" element={<AdminLayout />} />
            <Route path="/admin/docs" element={<AdminLayout />} />
            <Route path="/admin/settings" element={<AdminLayout />} />
            <Route path="/admin/content" element={<Navigate to="/admin/content/equipment" replace />} />
            <Route path="/admin/content/equipment" element={<AdminLayout />} />
            <Route path="/admin/content/exercises" element={<AdminLayout />} />
            <Route path="/admin/content/warmups" element={<AdminLayout />} />
            <Route path="/admin/content/finishers" element={<AdminLayout />} />
            <Route path="/admin/content/drills" element={<AdminLayout />} />
            <Route path="/admin/content/workouts" element={<AdminLayout />} />
            <Route path="/admin/*" element={<AdminLayout />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/signin" element={<AuthPage mode="signin" />} />
            <Route path="/launch" element={<AppEntry />} />
            <Route path="/onboarding" element={<OnboardingFlow />} />
            <Route path="/:userId/dashboard" element={<Dashboard />} />
            <Route path="/:userId/calendar" element={<Calendar />} />
            <Route path="/:userId/settings" element={<Settings />} />
            <Route path="/:userId/club/:clubId/manage" element={<ClubManage />} />
            <Route path="/:userId/club/:clubId/reports" element={<ClubReports />} />
            <Route path="/:userId/club/:clubId" element={<ClubDashboard />} />
            <Route path="/:userId/coach/:coachId" element={<CoachDashboard />} />
            <Route path="/:userId/renew" element={<Renew />} />
            <Route path="/:userId/program" element={<Program />} />
            <Route path="/:userId/change-program" element={<ChangeProgram />} />
            <Route path="/:userId/setup-program" element={<SetupProgram />} />
            <Route path="/:userId/performance" element={<Performance />} />
            <Route path="/:userId/awards" element={<Awards />} />
            <Route path="/:userId/notifications" element={<Notifications />} />
            <Route path="/:userId/workout" element={<Workout />} />
            <Route path="/:userId/workout/:sessionId" element={<Workout />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </div>
    </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;